import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  Alert,
  Avatar,
  Link,
  EditIcon,
  Icon,
  IconButton,
  InlineAlert,
  Pane,
  PersonIcon,
  SegmentedControl,
  Text,
} from "evergreen-ui";
import AutocompleteFormField from "../AutocompleteFormField";
import MatchPOButton from "./MatchPOButton";
import useControlHub from "../../hooks/useControlHub";

const InvoiceLink = ({ downloading, getInvoice }) => {
  if (downloading) return <Text color="muted">... downloading</Text>;

  return (
    <Link href="#" onClick={getInvoice}>
      Details
    </Link>
  );
};

const MatchInput = ({ invoiceID, onDataChange, po, bill }) => {
  const { api } = useControlHub();
  const [type, setType] = useState("3-way match");
  const [users, setUsers] = useState();
  const [downloading, setDownloading] = useState(false);

  useEffect(() => {
    api
      .getUsers()
      .then((data) => {
        if (!data) {
          throw new Error("Couldn't fetch users");
        }
        setUsers(data);
      })
      .catch(() => {});
  }, [api]);

  useEffect(() => {
    if (po?.["three_way_match"]) {
      setType("3-way match");
    } else if (po?.["biz_owner"]) {
      setType("biz owner");
    }
  }, [po]);

  const handleMatchDelete = useCallback(() => {
    onDataChange({ po: {} });
  }, [onDataChange]);

  const handleMatchSuccess = useCallback(() => {
    onDataChange({ fetch: true });
  }, [onDataChange]);

  const isValidInvoiceInfo = useCallback(() => {
    if (!bill) return false;

    const { due, date, number } = bill;
    return due && date && number ? true : false;
  }, [bill]);

  const handleOwnerChange = useCallback(
    (owner) => {
      const newPoInfo = {};
      newPoInfo["biz_owner"] = owner ? owner.id : "";
      onDataChange({ po: newPoInfo });
    },
    [onDataChange]
  );

  const handleTypeChange = useCallback((value) => {
    setType(value);
  }, []);

  const userToString = useCallback(
    (user) => (user ? user["full_name"] || user["slack_full_name"] : ""),
    []
  );

  const owner = useMemo(() => {
    if (!users || !po?.["biz_owner"]) {
      return;
    }
    return users.find((user) => user.id === po["biz_owner"]);
  }, [po, users]);

  const ownerPane = useMemo(() => {
    return (
      <Pane alignItems="center" display="flex" flex="0 0 auto">
        {owner ? (
          <>
            <Avatar
              marginX={8}
              name={owner["full_name"] || owner["slack_full_name"]}
              src={owner["image_48"]}
            />
            <Text>
              {owner["display_name"] ||
                owner["full_name"] ||
                owner["slack_full_name"]}
            </Text>
          </>
        ) : (
          <>
            <Icon color="muted" icon={PersonIcon} marginX={8} />
            <Text>-</Text>
          </>
        )}
      </Pane>
    );
  }, [owner]);

  const getInvoice = useCallback(
    (e) => {
      e.preventDefault();
      if (!bill) return;
      const { number } = bill;

      setDownloading(true);
      api
        .getFullInvoice(invoiceID, `invoice.${number}.pdf`)
        .catch((error) => {
          console.error(error);
        })
        .finally(() => {
          setDownloading(false);
        });
    },
    [invoiceID, api, bill]
  );

  const toggleOptions = useMemo(() => {
    return [
      {
        label: po?.["three_way_match"] ? "Matched to PO" : "Match to PO",
        value: "3-way match",
      },
      { label: "Business Owner", value: "biz owner" },
    ];
  }, [po]);

  const threeWayMatch = useMemo(() => {
    if (type !== "3-way match") {
      return null;
    }

    return (
      <>
        <Pane>
          <Pane
            alignItems="center"
            display="flex"
            justifyContent="space-between"
          >
            {po?.["three_way_match"] ? (
              <Pane alignItems="center" display="flex">
                <Text aria-label="PO Number">{`PO#: ${
                  po["po_number"] || "-"
                }`}</Text>
                <IconButton
                  icon={EditIcon}
                  marginX={8}
                  onClick={handleMatchDelete}
                />
              </Pane>
            ) : (
              <MatchPOButton
                invoiceID={invoiceID}
                onSuccess={handleMatchSuccess}
              />
            )}
            {ownerPane}
          </Pane>
        </Pane>
        <hr />
        <Pane display="flex" justifyContent="space-between" paddingY={8}>
          {po?.["three_way_match"] ? (
            <>
              <Pane display="flex" flex="1 0 50%" flexDirection="column">
                <InlineAlert
                  intent={
                    po["three_way_match"]["po"]["verdict"]
                      ? "success"
                      : "danger"
                  }
                  marginBottom={8}
                >
                  PO
                  {po["three_way_match"]["po"]["reason"] ? (
                    <>
                      <br />
                      {`(${po["three_way_match"]["po"]["reason"]})`}
                    </>
                  ) : null}
                </InlineAlert>
                <InlineAlert
                  intent={
                    po["three_way_match"]["receiving"]["verdict"]
                      ? "success"
                      : "danger"
                  }
                  marginBottom={8}
                >
                  Receiving
                  {po["three_way_match"]["receiving"]["reason"] ? (
                    <>
                      <br />
                      {`(${po["three_way_match"]["receiving"]["reason"]})`}
                    </>
                  ) : null}
                </InlineAlert>
                <InlineAlert
                  intent={
                    po["three_way_match"]["invoice"]["verdict"]
                      ? "success"
                      : "danger"
                  }
                  marginBottom={8}
                >
                  Invoice Amount
                  {po["three_way_match"]["invoice"]["reason"] ? (
                    <>
                      <br />
                      {`(${po["three_way_match"]["invoice"]["reason"]})`}
                    </>
                  ) : null}
                </InlineAlert>
              </Pane>
              <Pane flex="1 0 50%">
                {po["three_way_match"]["po"]["verdict"] &&
                po["three_way_match"]["receiving"]["verdict"] &&
                po["three_way_match"]["invoice"]["verdict"] ? (
                  <Alert intent="success" title={"3-way Match"}>
                    {isValidInvoiceInfo() && (
                      <InvoiceLink
                        getInvoice={getInvoice}
                        downloading={downloading}
                      />
                    )}
                  </Alert>
                ) : (
                  <Alert intent="warning" title={"Mismatch"}>
                    {isValidInvoiceInfo() && (
                      <InvoiceLink
                        getInvoice={getInvoice}
                        downloading={downloading}
                      />
                    )}
                  </Alert>
                )}
              </Pane>
            </>
          ) : (
            <Text color="muted" size={300}>
              No PO has been selected.
            </Text>
          )}
        </Pane>
      </>
    );
  }, [
    downloading,
    getInvoice,
    handleMatchDelete,
    handleMatchSuccess,
    invoiceID,
    isValidInvoiceInfo,
    ownerPane,
    po,
    type,
  ]);

  const bizOwnerMatch = useMemo(() => {
    if (!users || type !== "biz owner") {
      return null;
    }

    return (
      <Pane>
        <Pane display="flex" justifyContent="space-between">
          <AutocompleteFormField
            items={users}
            itemToString={userToString}
            label="Business Owner"
            onChange={handleOwnerChange}
            value={
              po?.["biz_owner"] && users
                ? users.find((user) => user.id === po["biz_owner"])
                : null
            }
          />
          {ownerPane}
        </Pane>
      </Pane>
    );
  }, [handleOwnerChange, ownerPane, po, type, userToString, users]);

  return (
    <Pane>
      <SegmentedControl
        options={toggleOptions}
        onChange={handleTypeChange}
        value={type}
      />
      <Pane marginTop={24} padding={8}>
        {threeWayMatch}
        {bizOwnerMatch}
      </Pane>
    </Pane>
  );
};

export default MatchInput;
